<script lang="ts" setup>
import { ref } from 'vue';
import ReportSubmittedIcon from '~/assets/img/icon/report-submitted.svg';
import AppConfirmationDialog from '~/components/AppConfirmationDialog.vue';
import { AppButton, AppTextArea, AppTypography } from '~/components/base';
import AppNumberField from '~/components/base/AppNumberField.vue';
import TimelinePreference from '~/components/TimelinePreference.vue';
import type { SubmitTender } from '~/types/SubmitTender';

const { VALIDATIONS } = useValidation();
const { data: responseData, pending: isLoading, execute: execSubmit, error: serverError } = useSubmit<{}>();
const submitTenderSuccessful = ref<boolean>(false);

const { jobId = 0 } = defineProps<{
  jobId: number;
}>();

const emits = defineEmits<{
  (e: 'close-confirmation-dialog-action'): void;
}>();

const { form, handleSubmit, getErrors, v$ } = useForm<SubmitTender>({
  form: {
    rate: null,
    availability: null,
    availability_date: null,
    notes: null,
    is_licensed_insured: false,
  },
  rules: {
    rate: {
      required: VALIDATIONS.required('Offer'),
    },
    availability: {
      required: VALIDATIONS.required('Availability'),
    },
    notes: {
      required: VALIDATIONS.required('Notes'),
    },
  },
});

const availabilityOptions = [
  { title: 'ASAP', value: 'asap' },
  { title: 'Flexible', value: 'flexible' },
  { title: 'Choose Date', value: 'choose_date' },
];

const showDatePicker = ref(false);

const formattedDeadlineDate = computed(() => {
  if (!form.value.availability_date) return null;
  return new Date(form.value.availability_date).toLocaleDateString();
});

const errors = computed(() => {
  return getErrors(serverError.value?.errors);
});

async function onSubmit() {
  handleSubmit(async function () {
    await execSubmit('/bids', {
      method: 'POST',
      body: {
        post_id: jobId,
        ...form.value,
      },
    });

    if (responseData.value) {
      submitTenderSuccessful.value = true;
    }
  });
}
</script>

<template>
  <v-card class="pa-4 pa-sm-6 bg-grey-lighten-4" flat>
    <AppTypography element="h5" type="title-5" class="mb-2">Submit Offer</AppTypography>
    <AppTypography element="p" type="subheadline-1" class="text-grey-darken-1 mb-4 mb-sm-6">
      Secure Your Next Job – Submit Your Best Offer & Availability
    </AppTypography>

    <v-form ref="tender-form" data-testid="tender-form" @submit.prevent="onSubmit">
      <!-- Rate Input -->
      <AppNumberField
        v-model="form.rate"
        label="Enter your offer"
        prefix="$"
        suffix-text="/ Per Hour"
        hide-details="auto"
        class="mb-4 rate-input"
        type="number"
        :error-messages="errors?.rate"
      />

      <div class="mb-4">
        <AppTypography element="p" type="body-3" class="mb-4">Availability</AppTypography>

        <div class="deadline-options d-flex">
          <TimelinePreference v-model="form.availability" v-model:timeline-date="form.availability_date" />
        </div>
        <div v-if="errors?.availability || errors?.availability_date" class="text-error text-caption mt-2 ml-3">
          {{ errors?.availability || errors?.availability_date }}
        </div>
      </div>

      <!-- Notes -->
      <AppTextArea
        v-model="form.notes"
        label="Notes"
        hide-details="auto"
        rows="3"
        :max-length="280"
        class="mb-4"
        :error-messages="errors?.notes"
        count
      />

      <!-- License Checkbox -->
      <v-card class="mb-6 pl-1 checkbox-card" flat>
        <v-checkbox
          v-model="form.is_licensed_insured"
          label="I confirm I am licensed & insured."
          color="black"
          hide-details
        />
      </v-card>

      <!-- Submit Button -->
      <AppButton color="yellow-darken-1" type="submit" :loading="isLoading" block> Submit Offer </AppButton>
    </v-form>

    <AppConfirmationDialog
      v-model="submitTenderSuccessful"
      :icon="ReportSubmittedIcon"
      title="Offer Submitted!"
      description="Your bid has been sent successfully."
      btn-text="Check my bids"
      @button-action="
        navigateTo('/my-bids');
        submitTenderSuccessful = false;
      "
      @close-action="emits('close-confirmation-dialog-action')"
    />
  </v-card>
</template>

<style scoped lang="scss">
@use '#core/assets/scss/partials/fonts' as *;
@use '#core/assets/scss/partials/colors' as *;

.deadline-options {
  overflow-x: auto;
  flex-wrap: nowrap;
  gap: 8px;
  padding-bottom: 4px;
  margin: 0 -8px;
  padding-left: 8px;
  padding-right: 8px;

  // Hide scrollbar for cleaner look
  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (min-width: 600px) {
    flex-wrap: wrap;
    overflow-x: visible;
    gap: 12px;
    margin: 0;
    padding: 0;
    padding-bottom: 0;
  }
}

.deadline-btn {
  text-transform: none;
  font-weight: 500;
  letter-spacing: normal;
  flex-shrink: 0;
  background-color: white !important;
  min-width: fit-content;

  .deadline-btn-text {
    white-space: nowrap;
    font-size: 0.875rem;

    @media (min-width: 600px) {
      font-size: 1rem;
    }
  }

  &.v-btn--variant-flat {
    color: white;
  }

  &.deadline-btn--selected {
    border-color: #01011d !important;
    color: #01011d !important;
    background-color: transparent !important;
  }

  @media (max-width: 599px) {
    min-height: 40px !important;
    padding: 0 16px !important;
  }
}

.back-button {
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 10;
  background-color: white !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.action-buttons {
  padding-top: 40px;

  :deep(.v-btn) {
    display: inline-flex;
    align-items: center;
    justify-content: center;

    .v-btn__content {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  @media (max-width: 599px) {
    flex-direction: column;
    align-items: stretch !important;
    gap: 12px;

    .action-buttons-group {
      flex-direction: column;
      width: 100%;
      gap: 12px !important;

      .action-btn {
        width: 100%;
        margin-right: 0 !important;

        :deep(.v-btn) {
          width: 100%;
        }

        &:first-child {
          order: 2;
        }

        &:last-child {
          order: 1;
        }
      }
    }
  }

  :deep(.app-button .v-btn) {
    background-color: $brand-yellow !important;
    color: $dark-navy !important;

    &:hover {
      background-color: $brand-yellow-shade-01 !important;
    }
  }

  // Disabled button styling
  :deep(.v-btn--disabled) {
    .v-btn__content {
      color: #01011d !important;
    }

    // Specific styling for upload button when disabled
    &.action-btn {
      .v-btn__content {
        color: #01011d !important;
      }
    }
  }
}

// Date Picker Popup Styling
:deep(.date-picker-popup) {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 16px !important;
  overflow: hidden;
}
</style>
